import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { PageBackground } from "@/components/ui/page-background";
import { AppHeader } from "@/components/app-header";
import { 
  ArrowLeft, 
  Star, 
  Users, 
  MessageSquare, 
  TrendingUp, 
  BarChart3,
  ThumbsUp,
  ThumbsDown
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { format } from "date-fns";

interface Event {
  id: number;
  name: string;
  date: string;
  venue: string;
  type: string;
  feedbackEnabled: boolean;
}

interface FeedbackItem {
  id: number;
  eventId: number;
  name: string | null;
  enjoymentRating: number | null;
  wouldAttendNext: boolean;
  feedback: string | null;
  submittedAt: string;
}

interface FeedbackStats {
  totalResponses: number;
  avgEnjoyment: number | null;
  avgHoursSpent: number | null;
  wouldAttendNext: {
    yes: number;
    no: number;
    percentage: number;
  };
  performedOnStage: {
    yes: number;
    no: number;
    didntPerform: number;
  };
}

interface FeedbackData {
  event: Event;
  feedback: FeedbackItem[];
  stats: FeedbackStats;
}

export default function EventFeedback() {
  const { eventId } = useParams<{ eventId: string }>();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  
  const currentEventId = eventId ? parseInt(eventId) : null;
  
  // Check if user has access to this event
  const { data: feedbackData, isLoading, error } = useQuery<FeedbackData>({
    queryKey: [`/api/feedback/event/${currentEventId}`],
    enabled: !!currentEventId,
    staleTime: 0
  });

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'PPP');
  };

  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), 'PPp');
  };

  const getEnjoymentColor = (rating: number) => {
    if (rating === 1) return 'text-red-500';
    if (rating === 2) return 'text-orange-500';
    if (rating === 3) return 'text-yellow-500';
    if (rating === 4) return 'text-blue-500';
    return 'text-green-500';
  };

  const getEnjoymentEmoji = (rating: number) => {
    if (rating === 1) return '😞';
    if (rating === 2) return '😕';
    if (rating === 3) return '😐';
    if (rating === 4) return '😊';
    return '🎉';
  };

  if (isLoading) {
    return (
      <PageBackground>
        <AppHeader username={user?.username} />
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <div className="text-lg text-slate-900 drop-shadow-sm">Loading feedback data...</div>
          </div>
        </div>
      </PageBackground>
    );
  }

  if (error || !feedbackData) {
    return (
      <PageBackground>
        <AppHeader username={user?.username} />
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <h2 className="text-xl font-semibold mb-2 text-slate-900 drop-shadow-sm">Access Denied</h2>
            <p className="text-slate-600 mb-4 drop-shadow-sm">
              You don't have permission to view feedback for this event.
            </p>
            <Button onClick={() => setLocation('/')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </PageBackground>
    );
  }

  const { event, feedback, stats } = feedbackData;

  return (
    <PageBackground>
      <AppHeader username={user?.username} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 relative z-10">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4 mt-2 -mb-2">
            <Button onClick={() => setLocation('/')} variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />

            </Button>
            <div>
              <h1 className="text-3xl font-bold drop-shadow-sm">
                Event Feedback
              </h1>
              <p className="text-slate-600 drop-shadow-sm">
                {event.name} • {formatDate(event.date)}
              </p>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="grid gap-6 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Responses</CardTitle>
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalResponses}</div>
                <p className="text-xs text-muted-foreground">Feedback submissions</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Enjoyment</CardTitle>
                <Star className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.avgEnjoyment ? `${stats.avgEnjoyment}/5` : 'N/A'}
                </div>
                <p className="text-xs text-muted-foreground">Average rating</p>
              </CardContent>
            </Card>

          </div>

          {/* Detailed Analytics */}
          <div className="grid gap-6 md:grid-cols-1">

            {/* Return Interest */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Return Interest
                </CardTitle>
                <CardDescription>
                  Would attendees come to future events?
                </CardDescription>
              </CardHeader>
              <CardContent>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ThumbsUp className="h-4 w-4 text-green-500" />
                      <span className="text-sm">Would attend next</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{stats.wouldAttendNext.yes}</span>
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ 
                            width: `${stats.wouldAttendNext.percentage}%` 
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ThumbsDown className="h-4 w-4 text-red-500" />
                      <span className="text-sm">Would not attend</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{stats.wouldAttendNext.no}</span>
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-red-500 h-2 rounded-full" 
                          style={{ 
                            width: `${100 - stats.wouldAttendNext.percentage}%` 
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-2 border-t">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {stats.wouldAttendNext.percentage}%
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Positive return rate
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Individual Feedback */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Individual Feedback
              </CardTitle>
              <CardDescription>
                All feedback submissions for this event
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {feedback.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No feedback submissions yet
                  </div>
                ) : (
                  feedback.map((item) => (
                    <div key={item.id} className="p-4 rounded-lg bg-white/10 backdrop-blur-sm border border-white/10">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-4">
                          {item.enjoymentRating && (
                            <div className="flex items-center gap-1">
                              <Star className="h-4 w-4 text-yellow-500" />
                              <span className={`font-semibold ${getEnjoymentColor(item.enjoymentRating)}`}>
                                {item.enjoymentRating}/5 {getEnjoymentEmoji(item.enjoymentRating)}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground mb-1">
                            {formatDateTime(item.submittedAt)}
                            {item.name && (
                              <div className="text-xs text-slate-600 mt-1">
                                by {item.name}
                              </div>
                            )}
                          </div>
                          <Badge variant={item.wouldAttendNext ? "default" : "secondary"}>
                            {item.wouldAttendNext ? "Would return" : "Won't return"}
                          </Badge>
                        </div>
                      </div>
                      
                      
                      {item.feedback && (
                        <div className="mt-3 p-3 bg-white/5 rounded-lg">
                          <p className="text-sm whitespace-pre-wrap">{item.feedback}</p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </PageBackground>
  );
}